// orchestrator_fullstack/frontend/src/app/auth/rbac.ts

export type Role = "platform-admin" | "admin" | "operator" | "viewer";

export type Permission =
  // admin shell / platform
  | "admin.access"
  | "admin.overview.view"
  | "admin.observability.view"
  | "admin.providers.view"
  | "admin.providers.manage"
  | "admin.policies.view"
  | "admin.policies.manage"
  | "admin.oidc.view"
  | "admin.oidc.manage"
  | "admin.governance.view"
  | "admin.governance.manage"

  // app / tenant
  | "runs.view"
  | "ingress.submit"
  | "ingress.apps.view"
  | "ingress.apps.manage"
  | "threads.view"
  | "memory.manage"

  // workflow composer / flows
  | "flows.view"
  | "flows.run"
  | "flows.edit"
  | "flows.publish"

  // dangerous ops
  | "providers.delete"
  | "providers.rotate_key"
  | "ingress.rotate_key"
  | "ingress.revoke_connection"
  | "flows.delete"
  | "apps.delete"
  | "policies.delete"
  | "artifacts.purge";

export type AuthMe = {
  user: string | null;
  tenant: string | null;
  roles: string[];
  claims: Record<string, any>;
};

export type AuthState = {
  token: string | null;
  user: string | null;
  tenant: string | null;
  roles: Role[];
  claims: Record<string, any>;
  fetchedAt: number | null;
  needsProvisioning: boolean;
};

export const AUTH_STORAGE_KEY = "orchestrator.jwt";
export const AUTH_ME_CACHE_KEY = "orchestrator.authme";
export const AUTH_ME_TTL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Normalize raw roles from backend into canonical Role[]
 * - Accepts canonical strings: platform-admin, admin, operator, viewer
 * - Also accepts "tenant-admin" and normalizes it to "admin" (IDP-agnostic)
 */
export function normalizeRoles(raw: unknown): Role[] {
  const arr = Array.isArray(raw) ? raw : [];
  const out: Role[] = [];

  for (const r of arr) {
    const s0 = String(r || "").trim();
    if (!s0) continue;

    const s = s0.toLowerCase();
    if (s === "platform-admin") out.push("platform-admin");
    else if (s === "admin" || s === "tenant-admin") out.push("admin");
    else if (s === "operator") out.push("operator");
    else if (s === "viewer") out.push("viewer");
  }

  return Array.from(new Set(out));
}

export function hasRole(roles: Role[], want: Role | Role[]): boolean {
  const wants = Array.isArray(want) ? want : [want];
  for (const w of wants) if (roles.includes(w)) return true;
  return false;
}

const rolePerms: Record<Role, Set<Permission>> = {
  "platform-admin": new Set<Permission>([
    "admin.access",
    "admin.overview.view",
    "admin.observability.view",
    "admin.providers.view",
    "admin.providers.manage",
    "admin.policies.view",
    "admin.policies.manage",
    "admin.oidc.view",
    "admin.oidc.manage",
    "admin.governance.view",
    "admin.governance.manage",

    "runs.view",
    "ingress.submit",
    "ingress.apps.view",
    "ingress.apps.manage",
    "threads.view",
    "memory.manage",

    "flows.view",
    "flows.run",
    "flows.edit",
    "flows.publish",

    "providers.delete",
    "providers.rotate_key",
    "ingress.rotate_key",
    "ingress.revoke_connection",
    "flows.delete",
    "apps.delete",
    "policies.delete",
    "artifacts.purge",
  ]),

  admin: new Set<Permission>([
    "admin.access",
    "admin.overview.view",
    "admin.observability.view",
    "admin.providers.view",
    "admin.providers.manage",
    "providers.delete",
    "providers.rotate_key",
    //"admin.policies.view",
    //"admin.policies.manage",

    "runs.view",
    "ingress.submit",
    "ingress.apps.view",
    "ingress.apps.manage",
    "threads.view",
    "memory.manage",

    //"flows.view",
    //"flows.run",
    //"flows.edit",
    //"flows.publish",

    "ingress.rotate_key",
    "ingress.revoke_connection",
    //"flows.delete",
    "apps.delete",
    //"policies.delete",
  ]),

  operator: new Set<Permission>([
    // optionally show in admin nav only if you want
    "admin.observability.view",

    "runs.view",
    "ingress.submit",
    "ingress.apps.view",
    "ingress.apps.manage",
    "threads.view",

    "flows.view",
    "flows.run",
  ]),

  viewer: new Set<Permission>([
    "runs.view",
    "ingress.apps.view",
    "threads.view",
    "flows.view",
  ]),
};

export function can(roles: Role[], permission: Permission): boolean {
  for (const r of roles) {
    const set = rolePerms[r];
    if (set && set.has(permission)) return true;
  }
  return false;
}

/**
 * Convenience helper for cases where you want to guard by roles rather than permissions.
 */
export function hasAnyRole(roles: Role[], anyOf: Role[]): boolean {
  const set = new Set<Role>(roles);
  return anyOf.some((r) => set.has(r));
}

export function deriveFlags(roles: Role[]) {
  const isPlatformAdmin = hasRole(roles, "platform-admin");
  const isTenantAdmin = hasRole(roles, "admin");
  const isOperator = hasRole(roles, "operator");
  const isViewerOnly = roles.length === 0 || (roles.length === 1 && roles[0] === "viewer");
  return { isPlatformAdmin, isTenantAdmin, isOperator, isViewerOnly };
}